import { Link, useNavigate, useParams } from "react-router-dom";
import BackButton from "../../components/cmp/BackButton";
import NotionRenderer from "../renderer/NotionRenderer";
import { useCompleteLesson, useLearningProgress } from "../store";
import type { CourseData, CourseLesson } from "../types";
import { useCourse } from "../useCourses";
import { LoadingSpinner, NotFound } from "./ui";
import FormattedHelmet from "../../components/cmp/intl/FormattedHelmet";

export default function LessonView() {
  const { slug, lessonId } = useParams<{ slug: string; lessonId: string }>();
  const { course, isLoading, error, mutate } = useCourse(slug);
  const lesson = course?.lessons.find((l) => l.id === lessonId);

  if (isLoading) return <LoadingSpinner />;
  if (!course || !lesson) {
    return (
      <NotFound
        message="Lesson not found"
        error={error}
        onRetry={() => mutate()}
      />
    );
  }

  return (
    <div>
      <BackButton></BackButton>
      <FormattedHelmet id={lesson.title}></FormattedHelmet>
      <h3 className="mb-1 mt-2">{lesson.title}</h3>
      <p className="text-muted mb-4">{course.title}</p>

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body p-4" key={String(lesson.id)}>
          <NotionRenderer
            content={lesson.content as any}
            lessonId={lesson.id}
          />
        </div>
      </div>

      <LessonFooter course={course} lesson={lesson} />
    </div>
  );
}

function LessonFooter({
  course,
  lesson,
}: {
  course: CourseData;
  lesson: CourseLesson;
}) {
  const completeLesson = useCompleteLesson();
  const { progress } = useLearningProgress();
  const completed = progress[lesson.id]?.completed;

  const idx = course.lessons.findIndex((l) => l.id === lesson.id);
  const prev = idx > 0 ? course.lessons[idx - 1] : undefined;
  const next =
    idx >= 0 && idx < course.lessons.length - 1
      ? course.lessons[idx + 1]
      : undefined;

  return (
    <div className="d-flex align-items-center justify-content-between mt-2">
      <div>
        {prev && (
          <Link
            to={`/learning/${course.id}/${prev.id}`}
            className="btn btn-link btn-sm"
          >
            ← Previous
          </Link>
        )}
      </div>
      <div className="d-flex gap-2">
        {completed ? (
          <span className="badge badge-sm bg-success d-inline-flex align-items-center">
            ✓ Completed
          </span>
        ) : (
          <button
            className="btn btn-success btn-sm"
            onClick={() => completeLesson(lesson.id)}
          >
            Mark as Complete
          </button>
        )}
        {next && (
          <Link to={`/learning/${course.id}/${next.id}`} className="btn btn-primary btn-sm">
            Next →
          </Link>
        )}
      </div>
    </div>
  );
}
